import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import {
  Alert,
  AppLayout,
  Box,
  Button,
  Cards,
  ColumnLayout,
  ContentLayout,
  ExpandableSection,
  FormField,
  Header,
  Link,
  Multiselect,
  SpaceBetween,
  TextFilter,
} from "@amzn/awsui-components-react/polaris";

import { selectIsEffectiveAdmin, selectIsEffectiveSupportAdmin } from "../../utilities/adminUtils";
import { getEditableFields } from "../Templates/Utils/FormMetadata";
import { TEMPLATE_APP } from "../Templates/Constants";
import SideNav from "../../Home/SideNav";
import { captureMetrics } from "src/katalMetrics/metricsHelperMethods";
import { ACTION_METHODS, METRIC_TYPE, SITE_SERVICES } from "src/katalMetrics/metricsModels";
import { TASK_MANAGER_NAV_LABEL, TASK_MANAGER_ROUTE } from "./constants";
import { listDashboards, getLogoViewUrl, fetchAllApps } from "./api";
import CreateTaskManagerModal from "./CreateTaskManagerModal";

const toArray = (v) => (Array.isArray(v) ? v : v ? [v] : []);

/**
 * Standalone Task Manager landing page.
 *
 * Renders inside a Cloudscape AppLayout (shared side nav + content container).
 * Shows the task manager cards with a search bar + an expandable Filters
 * section (App Names / Organization), and a "Create Task Manager" action.
 *
 * Organization filter options mirror the App Marketplace: organizations come
 * from the form metadata field values. `organization` is an array on an app.
 */
const TaskManager = () => {
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [createVisible, setCreateVisible] = useState(false);
  const [editing, setEditing] = useState(null);
  const [navOpen, setNavOpen] = useState(true);
  const [logoUrls, setLogoUrls] = useState({});

  // App metadata (id -> { name, org: [], vp: [] }) powering the filters.
  const [appsById, setAppsById] = useState({});
  // Organization options sourced from form metadata (same as the marketplace).
  const [orgOptions, setOrgOptions] = useState([]);

  // Search + filter state.
  const [filterText, setFilterText] = useState("");
  const [selectedApps, setSelectedApps] = useState([]);
  const [selectedOrgs, setSelectedOrgs] = useState([]);

  const isPortalAdmin = useSelector(selectIsEffectiveAdmin);
  const isSupportAdmin = useSelector(selectIsEffectiveSupportAdmin);
  const canEdit = isPortalAdmin || isSupportAdmin;

  const history = useHistory();

  // Pass the dashboard through router state so the detail page can show its
  // name immediately, without waiting on (or depending on) a re-fetch.
  const openDashboard = (item) =>
    history.push(`${TASK_MANAGER_ROUTE}/${item.dashboard_id}`, { dashboard: item });

  const handleEdit = (item) => setEditing(item);

  const handleSaved = (updated) => {
    if (!updated) return;
    setCards((prev) =>
      prev.map((c) => (c.dashboard_id === updated.dashboard_id ? { ...c, ...updated } : c))
    );
    if (updated.logo) {
      getLogoViewUrl(updated.logo)
        .then((url) => {
          if (url) setLogoUrls((prev) => ({ ...prev, [updated.dashboard_id]: url }));
        })
        .catch(() => {});
    }
  };

  const resolveLogos = async (items) => {
    const entries = await Promise.all(
      items
        .filter((item) => item.logo)
        .map(async (item) => {
          try {
            const url = await getLogoViewUrl(item.logo);
            return url ? [item.dashboard_id, url] : null;
          } catch (e) {
            return null;
          }
        })
    );
    setLogoUrls(Object.fromEntries(entries.filter(Boolean)));
  };

  const loadCards = async () => {
    setLoading(true);
    setLoadError("");
    // Time how long it takes to fetch all dashboards, published as a Katal timer.
    const startedAt = Date.now();
    try {
      const result = await listDashboards();
      const items = Array.isArray(result) ? result : [];
      setCards(items);
      resolveLogos(items);
      captureMetrics(
        ACTION_METHODS.PAGE_LOAD_TIME,
        [
          {
            metricType: METRIC_TYPE.TIMER,
            metricName: "TaskManagerListDashboardsLoadTime",
            metricValue: Date.now() - startedAt,
            isMonitor: true,
          },
        ],
        SITE_SERVICES.TASK_MANAGER
      );
    } catch (e) {
      const message =
        e?.errors?.[0]?.message || e?.message || "Failed to load task managers.";
      setLoadError(message);
      setCards([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCards();

    // App metadata (from the shared local cache) for App Names + Organization filters.
    fetchAllApps()
      .then((apps) => {
        const map = {};
        (apps || []).forEach((a) => {
          map[String(a.app_id)] = {
            name: a.app_name,
            org: toArray(a.organization),
          };
        });
        setAppsById(map);
      })
      .catch(() => setAppsById({}));

    // Organizations come from form metadata — same source as the marketplace.
    getEditableFields()
      .then((fieldMetadata) => {
        setOrgOptions(fieldMetadata?.[TEMPLATE_APP.ORGANIZATION]?.values || []);
      })
      .catch(() => setOrgOptions([]));
  }, []);

  const handleCreated = (created) => {
    if (!created) return;
    setCards((prev) => [created, ...prev]);
    if (created.logo) {
      getLogoViewUrl(created.logo)
        .then((url) => {
          if (url) setLogoUrls((prev) => ({ ...prev, [created.dashboard_id]: url }));
        })
        .catch(() => {});
    }
  };

  const appOptions = useMemo(
    () =>
      Object.entries(appsById)
        .map(([id, meta]) => ({ label: meta.name || id, value: id }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [appsById]
  );

  const activeFilterCount = selectedApps.length + selectedOrgs.length;

  const filteredCards = useMemo(() => {
    const q = filterText.trim().toLowerCase();
    const appIdSet = new Set(selectedApps.map((o) => o.value));
    const orgSet = new Set(selectedOrgs.map((o) => o.value));

    return cards.filter((c) => {
      const matchesSearch =
        !q ||
        (c.name && c.name.toLowerCase().includes(q)) ||
        (c.description && c.description.toLowerCase().includes(q));
      if (!matchesSearch) return false;

      const ids = (c.app_ids || []).map((id) => String(id));
      const matchesApps = appIdSet.size === 0 || ids.some((id) => appIdSet.has(id));
      const matchesOrg =
        orgSet.size === 0 ||
        ids.some((id) => (appsById[id]?.org || []).some((o) => orgSet.has(o)));
      return matchesApps && matchesOrg;
    });
  }, [cards, filterText, selectedApps, selectedOrgs, appsById]);

  const clearFilters = () => {
    setFilterText("");
    setSelectedApps([]);
    setSelectedOrgs([]);
  };

  const content = (
    <ContentLayout
      header={
        <Header
          variant="h1"
          actions={
            <Button
              variant="primary"
              iconName="add-plus"
              onClick={() => setCreateVisible(true)}
            >
              Create Task Manager
            </Button>
          }
          counter={loading ? undefined : `(${cards.length})`}
        >
          {TASK_MANAGER_NAV_LABEL}
        </Header>
      }
    >
      <SpaceBetween size="m">
        {loadError && (
          <Alert type="error" header="Couldn't load task managers" dismissible onDismiss={() => setLoadError("")}>
            {loadError}
          </Alert>
        )}

        <TextFilter
          filteringText={filterText}
          filteringPlaceholder="Search dashboards..."
          filteringAriaLabel="Search dashboards"
          onChange={({ detail }) => setFilterText(detail.filteringText)}
        />

        <ExpandableSection
          headerText="Filters"
          headerCounter={activeFilterCount ? `(${activeFilterCount})` : undefined}
          variant="container"
        >
          <ColumnLayout columns={2} borders="none">
            <FormField label="App Names">
              <Multiselect
                selectedOptions={selectedApps}
                options={appOptions}
                onChange={({ detail }) => setSelectedApps(detail.selectedOptions)}
                filteringType="auto"
                placeholder="Select apps"
                tokenLimit={3}
                empty="No apps available"
              />
            </FormField>
            <FormField label="Organization">
              <Multiselect
                selectedOptions={selectedOrgs}
                options={orgOptions}
                onChange={({ detail }) => setSelectedOrgs(detail.selectedOptions)}
                filteringType="auto"
                placeholder="Select org"
                tokenLimit={3}
                empty="No organizations available"
              />
            </FormField>
          </ColumnLayout>
        </ExpandableSection>

        <Cards
          items={filteredCards}
          loading={loading}
          loadingText="Loading dashboards"
          trackBy="dashboard_id"
          cardDefinition={{
            header: (item) => (
              <SpaceBetween direction="horizontal" size="s" alignItems="center">
                {logoUrls[item.dashboard_id] && (
                  <img
                    src={logoUrls[item.dashboard_id]}
                    alt={`${item.name} logo`}
                    style={{ width: 48, height: 48, objectFit: "contain", borderRadius: 6 }}
                  />
                )}
                <Link
                  fontSize="heading-m"
                  href={`${TASK_MANAGER_ROUTE}/${item.dashboard_id}`}
                  onFollow={(e) => {
                    e.preventDefault();
                    openDashboard(item);
                  }}
                >
                  {item.name}
                </Link>
              </SpaceBetween>
            ),
            sections: [
              {
                id: "description",
                content: (item) =>
                  item.description ? (
                    <Box variant="p" color="text-body-secondary">
                      {item.description}
                    </Box>
                  ) : null,
              },
              {
                id: "meta",
                content: (item) => (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 16,
                    }}
                  >
                    <SpaceBetween direction="horizontal" size="l">
                      <span>
                        <Box variant="awsui-key-label" display="inline">
                          Apps
                        </Box>{" "}
                        <span>{item.app_ids && item.app_ids.length ? item.app_ids.length : 0}</span>
                      </span>
                      <span>
                        <Box variant="awsui-key-label" display="inline">
                          Created by
                        </Box>{" "}
                        <span>{item.created_by || "-"}</span>
                      </span>
                    </SpaceBetween>
                    {canEdit && (
                      <Button variant="inline-link" iconName="edit" onClick={() => handleEdit(item)}>
                        Edit
                      </Button>
                    )}
                  </div>
                ),
              },
            ],
          }}
          cardsPerRow={[{ cards: 1 }, { minWidth: 500, cards: 2 }, { minWidth: 900, cards: 3 }]}
          empty={
            <Box textAlign="center" color="inherit" padding={{ vertical: "xl" }}>
              <SpaceBetween size="s">
                <Box variant="strong" color="inherit">
                  {cards.length === 0 ? "No task managers yet" : "No matches"}
                </Box>
                <Box variant="p" color="inherit">
                  {cards.length === 0
                    ? "Create a task manager to get started."
                    : "No task managers match your search or filters."}
                </Box>
                {cards.length === 0 ? (
                  <Button onClick={() => setCreateVisible(true)}>Create Task Manager</Button>
                ) : (
                  <Button onClick={clearFilters}>Clear filters</Button>
                )}
              </SpaceBetween>
            </Box>
          }
        />
      </SpaceBetween>

      <CreateTaskManagerModal
        visible={createVisible}
        onDismiss={() => setCreateVisible(false)}
        onCreated={handleCreated}
      />

      <CreateTaskManagerModal
        visible={Boolean(editing)}
        dashboard={editing}
        onDismiss={() => setEditing(null)}
        onSaved={handleSaved}
      />
    </ContentLayout>
  );

  return (
    <AppLayout
      content={content}
      contentType="default"
      toolsHide={true}
      navigationOpen={navOpen}
      onNavigationChange={() => setNavOpen(!navOpen)}
      navigation={<SideNav activeHref={TASK_MANAGER_ROUTE} />}
    />
  );
};

export default TaskManager;
